import { Component, HostListener, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from './ui/icon.component';

import { ModalService } from '../services/modal.service';

@Component({
  selector: 'app-hero',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <section id="hero" class="relative min-h-[100vh] flex flex-col justify-center pt-24 pb-12 md:pt-28 md:pb-20 bg-[#111111] overflow-hidden">
      <!-- Gradient Fade -->
      <div class="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/95 to-transparent md:to-[#111111]/10 pointer-events-none"></div>

      <!-- Left Side Decorative Image (Inverted) with Parallax -->
      <div class="absolute top-0 left-0 w-full md:w-[50%] h-full z-0 pointer-events-none opacity-40 mix-blend-screen will-change-transform"
           [style.transform]="'translateY(' + (scrollY() * 0.05) + 'px)'">
         <img src="/images/fundo-1.svg" 
              class="w-full h-full object-cover md:object-contain object-left-top transform -scale-x-100" 
              alt=""
              aria-hidden="true"
              width="600"
              height="600">
      </div>

      <!-- Decorative Orange Blur -->
      <div class="absolute -bottom-24 -left-24 w-96 h-96 bg-[#FF5500] rounded-full blur-[128px] opacity-10"></div>

      <div class="container mx-auto px-4 md:px-6 relative z-10">
        <!-- Flex container to keep elements united in the center regardless of screen width -->
        <div class="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16 max-w-6xl mx-auto">
          
          <!-- Content -->
          <div class="w-full max-w-xl space-y-5 lg:space-y-6">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5500]/10 border border-[#FF5500]/20 text-[#FF5500] text-xs font-semibold tracking-wide uppercase">
              <span class="w-2 h-2 rounded-full bg-[#FF5500] animate-pulse"></span>
              Conformidade NR 36
            </div>

            <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-tight">
              Controle total da <br/>
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5500] to-[#FF8800]">Pausa Térmica.</span>
            </h1>
            
            <p class="text-lg md:text-xl text-gray-200 font-medium leading-tight">
              Reduza passivos trabalhistas e garanta conformidade<br>
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5500] to-[#FF8800]">com a NR 36.</span>
            </p>
            
            <p class="text-base text-gray-300 max-w-lg leading-relaxed">
              A solução automatizada que monitora pausas obrigatórias em ambientes frios e quentes, protege colaboradores e blinda sua empresa contra multas e ações trabalhistas.
            </p>

            <div class="flex flex-col sm:flex-row gap-3 pt-2">
              <button (click)="openModal()" class="bg-[#FF5500] hover:bg-[#E04800] text-[#111111] px-6 py-3 rounded-lg font-bold text-base transition-all shadow-lg shadow-[#FF5500]/20 hover:shadow-[#FF5500]/40 flex items-center justify-center gap-2">
                Falar com um Especialista
                <app-icon name="chevron-right" [size]="18"></app-icon>
              </button>
            </div>

            <div class="flex items-center gap-3 pt-6 border-t border-white/10">
               <div class="flex items-center text-[#FF5500]">
                 <app-icon name="check-circle" [size]="20" class="mr-2"></app-icon>
               </div>
               <p class="text-sm text-gray-300 font-medium">Empresas já reduziram ações trabalhistas em até <span class="text-white font-bold">97%</span> com a beetrack.</p>
            </div>
          </div>

          <!-- Image/Visual -->
          <div class="w-full max-w-md lg:max-w-[480px] relative mt-8 lg:mt-0 flex justify-center">
            <div class="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
              <!-- Overlay gradient -->
              <div class="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent z-10 opacity-40"></div>
              
              <!-- Industrial Image (Responsive) -->
              <img src="/images/hero-2-md.webp"
                   srcset="/images/hero-2-sm.webp 400w, /images/hero-2-md.webp 600w, /images/hero-2-828.webp 828w, /images/hero-2-lg.webp 1000w"
                   sizes="(max-width: 768px) 100vw, 480px"
                   alt="Trabalhador em ambiente industrial refrigerado" 
                   width="480"
                   height="600"
                   fetchpriority="high"
                   class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 filter saturate-50 contrast-125" />
              
              <!-- Floating UI Card Mockup -->
              <div class="absolute bottom-6 left-6 right-6 z-20 bg-[#1c1c1c]/95 backdrop-blur-md border border-white/10 p-4 md:p-5 rounded-xl shadow-lg">
                 <div class="flex items-center justify-between mb-3">
                   <div class="flex items-center gap-3">
                     <div class="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                       <app-icon name="thermometer" [size]="20"></app-icon>
                     </div>
                     <div>
                       <div class="text-[10px] text-gray-300 uppercase tracking-wider">Câmara Resfriada 02</div>
                       <div class="text-sm md:text-base font-bold text-white">09°C (Monitorado)</div>
                     </div>
                   </div>
                   <div class="px-2 py-1 rounded bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wide">LEGAL</div>
                 </div>
                 <div class="space-y-2">
                   <div class="flex justify-between text-sm text-gray-300">
                     <span>Tempo Restante</span>
                     <span class="text-white font-mono font-bold">00:45:00</span>
                   </div>
                   <div class="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                     <div class="bg-[#FF5500] h-full rounded-full w-3/4"></div>
                   </div>
                 </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  `
})
export class HeroComponent {
  modalService = inject(ModalService);
  scrollY = signal(0);

  @HostListener('window:scroll')
  onScroll() {
    this.scrollY.set(window.scrollY);
  }

  openModal() {
    this.modalService.open();
  }

  scrollToHow() {
    const el = document.getElementById('how-it-works');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }
}